import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  type ReactNode,
} from "react";
import toast from "react-hot-toast";
import { baseToastClassName } from "@/components/providers/toastStyles";
import { showErrorToast } from "@/src/utils/showErrorToast";

export type ToastTone = "error" | "success" | "info";

interface ToastContextValue {
  showToast: (message: string, tone?: ToastTone) => void;
}

const ToastContext = createContext<ToastContextValue | null>(null);

export function ToastProvider({ children }: { children: ReactNode }) {
  const showToast = useCallback((message: string, tone: ToastTone = "error") => {
    if (tone === "success") {
      toast.success(message);
      return;
    }
    if (tone === "info") {
      toast(message, {
        className: `${baseToastClassName} border-line bg-bg-card text-ink-primary`,
      });
      return;
    }
    showErrorToast(message);
  }, []);

  const value = useMemo(() => ({ showToast }), [showToast]);

  return <ToastContext.Provider value={value}>{children}</ToastContext.Provider>;
}

export function useToast(): ToastContextValue {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error("useToast must be used within ToastProvider");
  }
  return context;
}
